import React, {useEffect, useState} from 'react';
import {Camera, Check, ChevronLeft, ChevronRight, MessageCircle, Phone, Star, X} from 'lucide-react';
import {t} from "i18next";

const YellowPageOnboardingForm = ({setIsYellowPageOnboardingFormModalOpen}) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        entityType: '',
        entityOffering: '',
        ownerName: '',
        entityName: '',
        businessMobile: '',
        businessEmail: '',
        whatsappNumber: '',
        photos: [],
        description: '',
        experience: '',
        category: '',
        address: '',
        area: '',
        pinCode: '',
        termsAccepted: false,
        infoAccurate: false,
        allowContact: false
    });

    // Auto-save functionality
    useEffect(() => {
        const interval = setInterval(() => {
            localStorage.setItem('yellowPagesFormData', JSON.stringify(formData));
        }, 30000);
        return () => clearInterval(interval);
    }, [formData]);

    const updateFormData = (field, value) => {
        setFormData(prev => ({...prev, [field]: value}));
    };

    const nextStep = () => {
        if (currentStep < 6) setCurrentStep(prev => prev + 1);
    };

    const prevStep = () => {
        if (currentStep > 1) setCurrentStep(prev => prev - 1);
    };

    const handlePhotoUpload = (e) => {
        const files = Array.from(e.target.files);
        const validFiles = files.filter(file =>
            file.type.startsWith('image/') && file.size <= 5 * 1024 * 1024
        );

        validFiles.forEach(file => {
            const reader = new FileReader();
            reader.onload = (e) => {
                updateFormData('photos', [...formData.photos, e.target.result]);
            };
            reader.readAsDataURL(file);
        });
    };

    const removePhoto = (index) => {
        updateFormData('photos', formData.photos.filter((_, i) => i !== index));
    };

    const categories = [
        t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.CATEGORY.OPTIONS.SHOPPING_RETAIL"),
        t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.CATEGORY.OPTIONS.FOOD_BEVERAGES"),
        t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.CATEGORY.OPTIONS.BEAUTY_HEALTH"),
        t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.CATEGORY.OPTIONS.PROFESSIONAL_SERVICES"),
        t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.CATEGORY.OPTIONS.AUTOMOTIVE_TRANSPORT"),
        t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.CATEGORY.OPTIONS.EDUCATION_TRAINING"),
        t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.CATEGORY.OPTIONS.EVENTS_ENTERTAINMENT"),
        t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.CATEGORY.OPTIONS.TECHNOLOGY_IT"),
        t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.CATEGORY.OPTIONS.CONSTRUCTION_HOME"),
        t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.CATEGORY.OPTIONS.ELECTRONICS_ELECTRICAL"),
        t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.CATEGORY.OPTIONS.LOGISTICS_TRANSPORT"),
        t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.CATEGORY.OPTIONS.PRINTING_STATIONERY"),
        t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.CATEGORY.OPTIONS.OTHER"),
    ];

    const areas = [
        t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.AREA.OPTIONS.CENTRAL_BUSINESS_DISTRICT"),
        t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.AREA.OPTIONS.RESIDENTIAL_AREA_1"),
        t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.AREA.OPTIONS.RESIDENTIAL_AREA_2"),
        t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.AREA.OPTIONS.INDUSTRIAL_ZONE"),
        t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.AREA.OPTIONS.MARKET_AREA"),
        t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.AREA.OPTIONS.OTHER")
    ];

    const renderStep1 = () => (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-custom-bold text-gray-800 mb-2">
                    {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_1.TITLE")}
                </h2>
                <p className="text-gray-600 font-custom-semi-bold">
                    {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_1.SUBTITLE")}
                </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
                <button
                    onClick={() => updateFormData('entityType', 'business')}
                    className={`p-6 border-2 rounded-xl text-left transition-all ${
                        formData.entityType === 'business'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                    }`}
                >
                    <h3 className="text-lg font-custom-semi-bold mb-2">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_1.BUSINESS_ENTITY.TITLE")}
                    </h3>
                    <p className="text-gray-600 mb-3 font-custom-medium">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_1.BUSINESS_ENTITY.DESCRIPTION")}
                    </p>
                    <p className="text-sm text-gray-500 font-custom-medium-italic">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_1.BUSINESS_ENTITY.EXAMPLES")}
                    </p>
                </button>

                <button
                    onClick={() => updateFormData('entityType', 'service')}
                    className={`p-6 border-2 rounded-xl text-left transition-all ${
                        formData.entityType === 'service'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                    }`}
                >
                    <h3 className="text-lg font-custom-semi-bold mb-2">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_1.SERVICE_ENTITY.TITLE")}
                    </h3>
                    <p className="text-gray-600 mb-3 font-custom-medium">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_1.SERVICE_ENTITY.DESCRIPTION")}
                    </p>
                    <p className="text-sm text-gray-500 font-custom-medium-italic">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_1.SERVICE_ENTITY.EXAMPLES")}
                    </p>
                </button>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-custom-bold text-gray-800 mb-2">
                    {
                        formData.entityType === 'business'
                            ? t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.BUSINESS.TITLE")
                            : t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.SERVICE.TITLE")
                    }
                </h2>
            </div>

            {formData.entityType === 'business' ? (
                <div className="grid gap-4">
                    {[
                        {
                            value: 'products',
                            title: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.BUSINESS.PRODUCTS.TITLE"),
                            desc: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.BUSINESS.PRODUCTS.DESCRIPTION"),
                            examples: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.BUSINESS.PRODUCTS.EXAMPLES")
                        },
                        {
                            value: 'services',
                            title: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.BUSINESS.SERVICES.TITLE"),
                            desc: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.BUSINESS.SERVICES.DESCRIPTION"),
                            examples: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.BUSINESS.SERVICES.EXAMPLES")
                        },
                        {
                            value: 'both',
                            title: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.BUSINESS.BOTH.TITLE"),
                            desc: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.BUSINESS.BOTH.DESCRIPTION"),
                            examples: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.BUSINESS.BOTH.EXAMPLES")
                        }
                    ].map(option => (
                        <button
                            key={option.value}
                            onClick={() => updateFormData('entityOffering', option.value)}
                            className={`p-6 border-2 rounded-xl text-left transition-all ${
                                formData.entityOffering === option.value
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                            <h3 className="text-lg font-custom-semi-bold mb-2">
                                {option.title}
                            </h3>
                            <p className="text-gray-600 mb-3 font-custom-medium">
                                {option.desc}
                            </p>
                            <p className="text-sm text-gray-500 font-custom-medium-italic">
                                {option.examples}
                            </p>
                        </button>
                    ))}
                </div>
            ) : (
                <div className="grid gap-4">
                    {[
                        {
                            value: 'personal',
                            title: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.SERVICE.PERSONAL.TITLE"),
                            desc: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.SERVICE.PERSONAL.DESCRIPTION"),
                            examples: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.SERVICE.PERSONAL.EXAMPLES")
                        },
                        {
                            value: 'property',
                            title: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.SERVICE.PROPERTY.TITLE"),
                            desc: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.SERVICE.PROPERTY.DESCRIPTION"),
                            examples: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.SERVICE.PROPERTY.EXAMPLES")
                        },
                        {
                            value: 'professional',
                            title: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.SERVICE.PROFESSIONAL.TITLE"),
                            desc: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.SERVICE.PROFESSIONAL.DESCRIPTION"),
                            examples: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.SERVICE.PROFESSIONAL.EXAMPLES")
                        },
                        {
                            value: 'mixed',
                            title: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.SERVICE.MIXED.TITLE"),
                            desc: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.SERVICE.MIXED.DESCRIPTION"),
                            examples: t("YELLOW_PAGE.ONBOARDING_FORM.STEP_2.SERVICE.MIXED.EXAMPLES")
                        }
                    ].map(option => (
                        <button
                            key={option.value}
                            onClick={() => updateFormData('entityOffering', option.value)}
                            className={`p-6 border-2 rounded-xl text-left transition-all ${
                                formData.entityOffering === option.value
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 hover:border-gray-300'
                            }`}
                        >
                            <h3 className="text-lg font-custom-semi-bold mb-2">
                                {option.title}
                            </h3>
                            <p className="text-gray-600 mb-3 font-custom-medium">
                                {option.desc}
                            </p>
                            <p className="text-sm text-gray-500 font-custom-medium-italic">
                                {option.examples}
                            </p>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );

    const renderStep3 = () => (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-custom-bold text-gray-800 mb-2">
                    {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_3.TITLE")}
                </h2>
                <p className="text-gray-600 font-custom-semi-bold">
                    {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_3.SUBTITLE")}
                </p>
            </div>

            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-custom-bold text-gray-700 mb-2">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_3.OWNER_NAME.LABEL")}
                    </label>
                    <input
                        type="text"
                        value={formData.ownerName}
                        onChange={(e) => updateFormData('ownerName', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg font-custom-semi-bold focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={t("YELLOW_PAGE.ONBOARDING_FORM.STEP_3.OWNER_NAME.PLACEHOLDER")}
                    />
                </div>

                <div>
                    <label className="block text-sm font-custom-bold text-gray-700 mb-2">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_3.BUSINESS_NAME.LABEL")}
                    </label>
                    <input
                        type="text"
                        value={formData.entityName}
                        onChange={(e) => updateFormData('entityName', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg font-custom-semi-bold focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={t("YELLOW_PAGE.ONBOARDING_FORM.STEP_3.BUSINESS_NAME.PLACEHOLDER")}
                    />
                </div>

                <div>
                    <label className="block text-sm font-custom-bold text-gray-700 mb-2">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_3.BUSINESS_MOBILE.LABEL")}
                    </label>
                    <input
                        type="tel"
                        value={formData.businessMobile}
                        onChange={(e) => updateFormData('businessMobile', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg font-custom-semi-bold focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={t("YELLOW_PAGE.ONBOARDING_FORM.STEP_3.BUSINESS_MOBILE.PLACEHOLDER")}
                    />
                    <p className="text-sm text-gray-500 mt-1 font-custom-medium">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_3.BUSINESS_MOBILE.HELP_TEXT")}
                    </p>
                </div>

                <div>
                    <label className="block text-sm font-custom-bold text-gray-700 mb-2">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_3.BUSINESS_EMAIL.LABEL")}
                    </label>
                    <input
                        type="email"
                        value={formData.businessEmail}
                        onChange={(e) => updateFormData('businessEmail', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg font-custom-semi-bold focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={t("YELLOW_PAGE.ONBOARDING_FORM.STEP_3.BUSINESS_EMAIL.PLACEHOLDER")}
                    />
                    <p className="text-sm text-gray-500 mt-1 font-custom-medium">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_3.BUSINESS_EMAIL.HELP_TEXT")}
                    </p>
                </div>

                <div>
                    <label className="block text-sm font-custom-bold text-gray-700 mb-2">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_3.WHATSAPP_NUMBER.LABEL")}
                    </label>
                    <input
                        type="tel"
                        value={formData.whatsappNumber}
                        onChange={(e) => updateFormData('whatsappNumber', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg font-custom-semi-bold focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={t("YELLOW_PAGE.ONBOARDING_FORM.STEP_3.WHATSAPP_NUMBER.PLACEHOLDER")}
                    />
                    <p className="text-sm text-gray-500 mt-1 font-custom-medium">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_3.WHATSAPP_NUMBER.HELP_TEXT")}
                    </p>
                </div>
            </div>
        </div>
    );

    const renderStep4 = () => (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-custom-bold text-gray-800 mb-2">
                    {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.TITLE")}
                </h2>
                <p className="text-gray-600 font-custom-semi-bold">
                    {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.SUBTITLE")}
                </p>
            </div>

            <div className="space-y-6">
                {/* Photo Upload */}
                <div>
                    <label className="block text-sm font-custom-bold text-gray-700 mb-2">
                        {
                            formData.entityType === 'business'
                                ? t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.PHOTO_UPLOAD.BUSINESS_PHOTOS")
                                : t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.PHOTO_UPLOAD.SERVICE_PHOTOS")
                        }
                        {
                            formData.entityType === 'business' && formData.entityOffering === 'products'
                            && t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.PHOTO_UPLOAD.IMAGES_COUNT")
                        }
                    </label>

                    <div
                        className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                        <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handlePhotoUpload}
                            className="hidden"
                            id="photo-upload"
                        />
                        <label htmlFor="photo-upload" className="cursor-pointer">
                            <Camera className="mx-auto h-12 w-12 text-gray-400 mb-2"/>
                            <p className="text-gray-600 font-custom-semi-bold">
                                {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.PHOTO_UPLOAD.UPLOAD_TEXT")}
                            </p>
                            <p className="text-sm text-gray-500 mt-1 font-custom-medium">
                                {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.PHOTO_UPLOAD.FILE_REQUIREMENTS")}
                            </p>
                        </label>
                    </div>

                    {formData.photos.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                            {formData.photos.map((photo, index) => (
                                <div key={index} className="relative">
                                    <img src={photo} alt={`Upload ${index + 1}`}
                                         className="w-full h-24 object-cover rounded-lg"/>
                                    <button
                                        onClick={() => removePhoto(index)}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                    >
                                        <X className="h-4 w-4"/>
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Description */}
                <div>
                    <label className="block text-sm font-custom-bold text-gray-700 mb-2">
                        {formData.entityType === 'business'
                            ? (
                            formData.entityOffering === 'products'
                                ? t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.DESCRIPTION.WHAT_SELL")
                                : formData.entityOffering === 'services'
                                    ? t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.DESCRIPTION.WHAT_SERVICES")
                                    : t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.DESCRIPTION.WHAT_OFFER")
                        ) + ' *'
                            : t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.DESCRIPTION.WHAT_DO")
                        }
                    </label>
                    <textarea
                        value={formData.description}
                        onChange={(e) => updateFormData('description', e.target.value)}
                        rows={4}
                        maxLength={formData.entityOffering === 'both' ? 150 : 100}
                        className="w-full p-3 border border-gray-300 rounded-lg font-custom-semi-bold focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder={
                            formData.entityType === 'business'
                                ? (formData.entityOffering === 'products'
                                        ? t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.DESCRIPTION.PLACEHOLDER_PRODUCTS")
                                        : formData.entityOffering === 'services'
                                            ? t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.DESCRIPTION.PLACEHOLDER_SERVICES")
                                            : t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.DESCRIPTION.PLACEHOLDER_BOTH")
                                )
                                : t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.DESCRIPTION.PLACEHOLDER_SERVICE_ENTITY")
                        }
                    />
                    <p className="text-sm text-gray-500 mt-1 font-custom-medium">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.DESCRIPTION.CHARACTER_COUNT", {
                            current: formData.description.length,
                            max: formData.entityOffering === 'both' ? 150 : 100
                        })}
                    </p>
                </div>

                {/* Experience for Service Entity */}
                {formData.entityType === 'service' && (
                    <div>
                        <label className="block text-sm font-custom-bold text-gray-700 mb-2">
                            {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.EXPERIENCE.LABEL")}
                        </label>
                        <select
                            value={formData.experience}
                            onChange={(e) => updateFormData('experience', e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg font-custom-semi-bold focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">
                                {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.EXPERIENCE.PLACEHOLDER")}
                            </option>
                            <option value="<1">
                                {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.EXPERIENCE.OPTIONS.LESS_THAN_1")}
                            </option>
                            <option value="1-5">
                                {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.EXPERIENCE.OPTIONS.1_TO_5")}
                            </option>
                            <option value="5-10">
                                {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.EXPERIENCE.OPTIONS.5_TO_10")}
                            </option>
                            <option value="10+">
                                {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_4.EXPERIENCE.OPTIONS.MORE_THAN_10")}
                            </option>
                        </select>
                    </div>
                )}
            </div>
        </div>
    );

    const renderStep5 = () => (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-custom-bold text-gray-800 mb-2">
                    {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.TITLE")}
                </h2>
                <p className="text-gray-600 font-custom-semi-bold">
                    {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.SUBTITLE")}
                </p>
            </div>

            <div className="space-y-6">
                <div>
                    <label className="block text-sm font-custom-bold text-gray-700 mb-2">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.CATEGORY.LABEL")}
                    </label>
                    <select
                        value={formData.category}
                        onChange={(e) => updateFormData('category', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg font-custom-medium focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                        <option value="">
                            {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.CATEGORY.PLACEHOLDER")}
                        </option>
                        {categories.map(category => (
                            <option
                                key={category}
                                value={category}
                            >
                                {category}
                            </option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="block text-sm font-custom-bold text-gray-700 mb-2">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.ADDRESS.LABEL")}
                    </label>
                    <textarea
                        value={formData.address}
                        onChange={(e) => updateFormData('address', e.target.value)}
                        rows={3}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 font-custom-semi-bold focus:ring-blue-500 focus:border-transparent"
                        placeholder={t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.ADDRESS.PLACEHOLDER")}
                    />
                    <p className="text-sm text-gray-500 mt-1 font-custom-medium">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.ADDRESS.HELP_TEXT")}
                    </p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-custom-bold text-gray-700 mb-2">
                            {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.AREA.LABEL")}
                        </label>
                        <select
                            value={formData.area}
                            onChange={(e) => updateFormData('area', e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded-lg font-custom-medium focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">
                                {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.AREA.PLACEHOLDER")}
                            </option>
                            {areas.map(area => (
                                <option
                                    key={area}
                                    value={area}
                                >
                                    {area}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-custom-bold text-gray-700 mb-2">
                            {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.PIN_CODE.LABEL")}
                        </label>
                        <input
                            type="text"
                            value={formData.pinCode}
                            onChange={(e) => updateFormData('pinCode', e.target.value)}
                            maxLength={6}
                            className="w-full p-3 border border-gray-300 rounded-lg font-custom-semi-bold focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder={t("YELLOW_PAGE.ONBOARDING_FORM.STEP_5.PIN_CODE.PLACEHOLDER")}
                        />
                    </div>
                </div>
            </div>
        </div>
    );

    const renderStep6 = () => (
        <div className="space-y-6">
            <div className="text-center mb-8">
                <h2 className="text-2xl font-custom-bold text-gray-800 mb-2">
                    {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_6.TITLE")}
                </h2>
                <p className="text-gray-600 font-custom-semi-bold">
                    {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_6.SUBTITLE")}
                </p>
            </div>

            {/* Preview Card */}
            <div className="bg-white border-2 border-gray-200 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-custom-semi-bold mb-4">
                    {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_6.PREVIEW.TITLE")}
                </h3>

                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start gap-4">
                        {formData.photos.length > 0 ? (
                            <img src={formData.photos[0]} alt="Business"
                                 className="w-16 h-16 object-cover rounded-lg flex-shrink-0"/>
                        ) : (
                            <div
                                className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                                <Camera className="h-8 w-8 text-gray-400"/>
                            </div>
                        )}

                        <div className="flex-1">
                            <h4 className="font-custom-semi-bold text-gray-900">
                                {formData.entityName || t("YELLOW_PAGE.ONBOARDING_FORM.STEP_6.PREVIEW.DEFAULT_BUSINESS_NAME")}
                            </h4>
                            <p className="font-custom-medium text-sm text-gray-600">
                                {formData.category || t("YELLOW_PAGE.ONBOARDING_FORM.STEP_6.PREVIEW.DEFAULT_CATEGORY")} | {formData.area || t("YELLOW_PAGE.ONBOARDING_FORM.STEP_6.PREVIEW.DEFAULT_AREA")}
                            </p>
                            <p className="font-custom-medium text-sm text-gray-700 mt-1 line-clamp-2">
                                {formData.description || t("YELLOW_PAGE.ONBOARDING_FORM.STEP_6.PREVIEW.DEFAULT_DESCRIPTION")}
                            </p>

                            <div className="flex gap-4 mt-2">
                                {formData.businessMobile && (
                                    <div className="flex items-center gap-1 text-sm font-custom-medium text-blue-600">
                                        <Phone className="h-4 w-4"/>
                                        <span>{t("YELLOW_PAGE.ONBOARDING_FORM.STEP_6.PREVIEW.MOBILE")}</span>
                                    </div>
                                )}
                                {formData.whatsappNumber && (
                                    <div className="flex items-center gap-1 text-sm font-custom-medium text-green-600">
                                        <MessageCircle className="h-4 w-4"/>
                                        <span>{t("YELLOW_PAGE.ONBOARDING_FORM.STEP_6.PREVIEW.WHATSAPP")}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Checkboxes */}
            <div className="space-y-4">
                <label className="flex items-start gap-3">
                    <input
                        type="checkbox"
                        checked={formData.termsAccepted}
                        onChange={(e) => updateFormData('termsAccepted', e.target.checked)}
                        className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm font-custom-semi-bold text-gray-700">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_6.CHECKBOXES.TERMS_ACCEPTED")}
                    </span>
                </label>

                <label className="flex items-start gap-3">
                    <input
                        type="checkbox"
                        checked={formData.infoAccurate}
                        onChange={(e) => updateFormData('infoAccurate', e.target.checked)}
                        className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm font-custom-semi-bold text-gray-700">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_6.CHECKBOXES.INFO_ACCURATE")}
                    </span>
                </label>

                <label className="flex items-start gap-3">
                    <input
                        type="checkbox"
                        checked={formData.allowContact}
                        onChange={(e) => updateFormData('allowContact', e.target.checked)}
                        className="mt-1 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm font-custom-semi-bold text-gray-700">
                        {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_6.CHECKBOXES.ALLOW_CONTACT")}
                    </span>
                </label>
            </div>

            {/* Submission Buttons */}
            <div className="grid md:grid-cols-2 gap-4 pt-4">
                <button
                    className="w-full py-3 px-4 border border-gray-300 font-custom-medium text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                    {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_6.BUTTONS.SAVE_DRAFT")}
                </button>
                <button
                    disabled={!formData.termsAccepted || !formData.infoAccurate || !formData.allowContact}
                    className="w-full py-3 px-4 bg-blue-600 font-custom-medium text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                    <Star className="h-5 w-5"/>
                    {t("YELLOW_PAGE.ONBOARDING_FORM.STEP_6.BUTTONS.GO_LIVE")}
                </button>
            </div>
        </div>
    );

    const renderCurrentStep = () => {
        switch (currentStep) {
            case 1:
                return renderStep1();
            case 2:
                return renderStep2();
            case 3:
                return renderStep3();
            case 4:
                return renderStep4();
            case 5:
                return renderStep5();
            case 6:
                return renderStep6();
            default:
                return renderStep1();
        }
    };

    return (
        <div className="max-w-4xl mx-auto p-4 form-modal-container landscape:max-h-[95vh] overflow-hidden">
            {/* Header */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
                <div className="p-6 border-b border-gray-200 flex flex-row">
                    <div className={"flex-1 flex flex-col items-center justify-between"}>
                        <h1 className="text-3xl font-custom-bold text-gray-900 text-center mb-2">
                            {t("YELLOW_PAGE.ONBOARDING_FORM.HEADER.TITLE")}
                        </h1>

                        <p className="text-gray-600 text-center font-custom-semi-bold">
                            {t("YELLOW_PAGE.ONBOARDING_FORM.HEADER.SUBTITLE")}
                        </p>
                    </div>

                    {/* Close Button */}
                    <button
                        onClick={() => setIsYellowPageOnboardingFormModalOpen(false)}
                        className={`
                            flex items-center justify-center rounded-lg
                            bg-[#F5F5F5] hover:bg-[#F7F7F7] aspect-square
                        
                            portrait:hover:ring-4 landscape:hover:ring-2
                            landscape:jk:hover:ring-4 hover:ring-[#D4D4D4]
                            landscape:rounded-[0.2rem] landscape:jk:rounded-md
                            transition duration-300 ease-out
                        
                            portrait:w-[2rem] portrait:md:w-[1.75rem]
                            portrait:jk:w-[2rem] portrait:lg:w-[2.25rem]
                        
                            landscape:w-[0.75rem] landscape:sm:w-[0.95rem]
                            landscape:jk:w-[1.25rem] landscape:lg:w-[1.75rem]
                            
                            portrait:h-[2rem] portrait:md:h-[1.75rem]
                            portrait:jk:h-[2rem] portrait:lg:h-[2.25rem]
                        
                            landscape:h-[0.75rem] landscape:sm:h-[0.95rem]
                            landscape:jk:h-[1.25rem] landscape:lg:h-[1.75rem]
                        `}
                    >
                        <img
                            className={"w-[75%] h-[75%]"}
                            src={"/assets/close-icon.png"}
                            alt={"Close"}
                            title={"Close"}
                        />
                    </button>
                </div>

                {/* Train Progress Indicator */}
                <div className="px-6 py-4">
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-custom-medium text-gray-700">
                            {
                                t("YELLOW_PAGE.ONBOARDING_FORM.PROGRESS.STEP_OF", {
                                    current: currentStep,
                                    total: 6
                                })
                            }
                        </span>
                        <span className="text-sm text-gray-500 font-custom-medium">
                            {
                                t("YELLOW_PAGE.ONBOARDING_FORM.PROGRESS.PERCENT_COMPLETE", {
                                    percent: Math.round((currentStep / 6) * 100)
                                })
                            }
                        </span>
                    </div>

                    {/* Train Stops */}
                    <div className="relative">
                        {/* Railway Track */}
                        <div className="absolute top-4 left-0 w-full h-0.5 bg-gray-300"></div>
                        <div
                            className="absolute top-4 left-0 h-0.5 bg-blue-600 transition-all duration-500"
                            style={{width: `${((currentStep - 1) / 5) * 100}%`}}
                        />

                        {/* Train Stops */}
                        <div className="flex justify-between relative">
                            {[
                                {num: 1, label: t("YELLOW_PAGE.ONBOARDING_FORM.PROGRESS.STEPS.ENTITY")},
                                {num: 2, label: t("YELLOW_PAGE.ONBOARDING_FORM.PROGRESS.STEPS.TYPE")},
                                {num: 3, label: t("YELLOW_PAGE.ONBOARDING_FORM.PROGRESS.STEPS.DETAILS")},
                                {num: 4, label: t("YELLOW_PAGE.ONBOARDING_FORM.PROGRESS.STEPS.OFFERS")},
                                {num: 5, label: t("YELLOW_PAGE.ONBOARDING_FORM.PROGRESS.STEPS.LOCATION")},
                                {num: 6, label: t("YELLOW_PAGE.ONBOARDING_FORM.PROGRESS.STEPS.REVIEW")}
                            ].map((step, index) => (
                                <div key={step.num} className="flex flex-col items-center">
                                    <div
                                        className={`w-8 h-8 rounded-full border-2 flex items-center justify-center text-sm font-custom-medium transition-all duration-300 ${
                                            currentStep > step.num
                                                ? 'bg-blue-600 border-blue-600 text-white'
                                                : currentStep === step.num
                                                    ? 'bg-blue-100 border-blue-600 text-blue-600'
                                                    : 'bg-white border-gray-300 text-gray-400'
                                        }`}>
                                        {currentStep > step.num ? (
                                            <Check className="h-4 w-4"/>
                                        ) : (
                                            step.num
                                        )}
                                    </div>
                                    <span
                                        className={`text-xs mt-2 text-center font-custom-medium ${
                                            currentStep >= step.num ? 'text-gray-700' : 'text-gray-400'
                                        }`}
                                    >
                                        {step.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Form Content */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-scroll thin-scrollbar">
                <div className="p-6">
                    {renderCurrentStep()}
                </div>

                {/* Navigation */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-between">
                    <button
                        onClick={prevStep}
                        disabled={currentStep === 1}
                        className="flex items-center gap-2 px-4 py-2 font-custom-semi-bold text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <ChevronLeft className="h-5 w-5"/>
                        {t("YELLOW_PAGE.ONBOARDING_FORM.NAVIGATION.PREVIOUS")}
                    </button>

                    <button
                        onClick={nextStep}
                        disabled={currentStep === 6}
                        className="flex items-center gap-2 px-6 py-2 bg-blue-600 font-custom-semi-bold text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {t("YELLOW_PAGE.ONBOARDING_FORM.NAVIGATION.NEXT")}
                        <ChevronRight className="h-5 w-5"/>
                    </button>
                </div>
            </div>

            {/* Help Button */}
            {/*<button*/}
            {/*    className="fixed bottom-6 right-6 bg-blue-600 text-white p-3 rounded-full shadow-lg hover:bg-blue-700 transition-colors">*/}
            {/*    <HelpCircle className="h-6 w-6"/>*/}
            {/*</button>*/}
        </div>
    );
};

export default YellowPageOnboardingForm;
